import React from 'react';
import { TemplateError } from '@tymely/api';
import {
    useArgumentsMetadataQuery,
    useArgumentsQuery,
    useGetPromptNamesQuery,
    useRenderTemplateQuery,
    useTemplateFreeVariablesQuery,
    useTicketOfflineQuery,
} from '@tymely/services';
import { Option } from '@tymely/components';
import { Nullable } from '@global/types';
import { useDebounce } from '@tymely/utils';
import {
    IArgumentMetadata,
    IPolicySet,
    IOrganization,
    IResponseTemplate,
    ITemplateVariable,
    isCategorical,
} from '@tymely/atoms';

type UseCreateTemplateEditorArgs = {
    templateId?: IResponseTemplate['id'];
    template?: IResponseTemplate['response_txt'];
    organizationId?: IOrganization['id'];
    intentId?: IPolicySet['id'];
    isModified?: boolean;
    diff?: Diff.Change[];
    sanitize?: boolean;
    error?: TemplateError;
    onChange?: (template: IResponseTemplate['response_txt'], varNames: string[]) => void;
    templateObj?: IResponseTemplate;
};

const useCreateTemplateEditor = ({
    template,
    templateObj,
    organizationId,
    intentId,
    sanitize,
    error,
    onChange,
    isModified,
    ...rest
}: UseCreateTemplateEditorArgs) => {
    const [variables, setVariables] = React.useState<Record<string, ITemplateVariable>>({});
    const [comment, setComment] = React.useState<Nullable<Option<number>[]>>([]);
    const [ticketId, setTicketId] = React.useState<number>();
    const { data: promptNames = [] } = useGetPromptNamesQuery();

    React.useEffect(() => {
        const unloadCallback = (event: BeforeUnloadEvent) => {
            event.preventDefault();
            event.returnValue = '';
            return '';
        };

        if (isModified) {
            window.addEventListener('beforeunload', unloadCallback);
            return () => window.removeEventListener('beforeunload', unloadCallback);
        }
        return;
    }, [isModified]);

    const ticketOfflineQuery = useTicketOfflineQuery(ticketId, !!ticketId);

    const argumentsQuery = useArgumentsQuery({
        commentId: comment ? comment[0]?.value : undefined,
        argsVersion: undefined,
        intentArgsOnly: false,
    });

    const argumentsMetadataQuery = useArgumentsMetadataQuery();
    const templateForFreeVars = useDebounce(template ?? '', 2000);
    const freeVariablesQuery = useTemplateFreeVariablesQuery(templateForFreeVars, organizationId, intentId);

    const argMetadata = React.useMemo(
        () =>
            argumentsMetadataQuery.data?.reduce<Record<string, IArgumentMetadata>>(
                (acc, item) => ({ ...acc, [item.name]: item }),
                {},
            ),
        [argumentsMetadataQuery.data],
    );

    const argsVariables = React.useMemo(() => {
        if (!argumentsQuery.data) {
            return variables;
        }
        return argumentsQuery.data.reduce(
            (vars, arg) => {
                vars[arg.name] = {
                    ...arg,
                    ...vars[arg.name],
                    categories: isCategorical(arg) ? arg.categories : undefined,
                    value: vars[arg.name]?.value || arg.value,
                };
                return vars;
            },
            { ...variables },
        );
    }, [variables, argumentsQuery.data]);

    const metaVars = React.useMemo(() => {
        return freeVariablesQuery.data?.reduce<Record<string, ITemplateVariable>>(
            (acc, varName) => ({
                ...acc,
                [varName]: {
                    value: '',
                    dtype: argMetadata?.[varName]?.dtype ?? 'str',
                    arg_type: argMetadata?.[varName]?.arg_type ?? 'VARIABLE',
                    is_list: argMetadata?.[varName]?.is_list ?? false,
                    special_value: argsVariables[varName]?.special_value,
                    group_by: argsVariables[varName]?.group_by,
                },
            }),
            {},
        );
    }, [argMetadata, argsVariables, freeVariablesQuery.data]);

    const argsVariablesWithMeta = React.useMemo(() => {
        if (!metaVars) {
            return argsVariables;
        }
        return Object.keys(metaVars).reduce(
            (vars, varName) => {
                vars[varName] = { ...metaVars[varName], ...argsVariables[varName] };
                return vars;
            },
            { ...metaVars } as Record<string, ITemplateVariable>,
        );
    }, [argsVariables, metaVars]);

    const templateToRender = useDebounce(wrapGuideline(template || '', promptNames), 300) || '';
    const renderTemplateQuery = useRenderTemplateQuery(
        templateToRender,
        argsVariablesWithMeta,
        sanitize,
        Boolean(templateToRender) && !argumentsQuery.isLoading && !ticketOfflineQuery.isLoading,
        organizationId,
        intentId,
    );

    const templateErrors = React.useMemo(() => {
        if (renderTemplateQuery.error instanceof TemplateError) {
            return renderTemplateQuery.error.detail;
        }
        if (error) {
            return error.detail;
        }
        return [];
    }, [error, renderTemplateQuery.error]);

    const onCodeChange = (code: string) => {
        onChange?.(code, Object.keys(argsVariablesWithMeta));
    };

    React.useEffect(() => {
        if (template) {
            // Trigger onChange when variables fetched (after template changed)
            onCodeChange(template);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [argsVariablesWithMeta, template]);

    return {
        ticketOfflineQuery,
        argumentsQuery,
        argumentsMetadataQuery,
        freeVariablesQuery,
        renderTemplateQuery,
        ticketId,
        templateErrors,
        setTicketId,
        variables,
        argsVariables: argsVariablesWithMeta,
        comment,
        setComment,
        setVariables,
        onCodeChange,
        template,
        organizationId,
        intentId,
        templateObj,
        ...rest,
    };
};

export type TemplateEditorContext = ReturnType<typeof useCreateTemplateEditor>;

export const templateEditorContext = React.createContext<TemplateEditorContext | undefined>(undefined);
const editorContext = templateEditorContext;

export type TemplateEditorProviderProps = React.PropsWithChildren<UseCreateTemplateEditorArgs>;

const TemplateEditorProvider = ({ children, ...rest }: TemplateEditorProviderProps) => {
    const value = useCreateTemplateEditor(rest);
    return <editorContext.Provider value={value}>{children}</editorContext.Provider>;
};

export const useTemplateEditor = () => {
    const context = React.useContext(editorContext);
    if (!context) {
        throw Error('`useTemplateEditor` must be used only inside `TemplateEditorProvider`');
    }
    return context;
};

export default TemplateEditorProvider;

function wrapGuideline(html: string, promptNames: string[]) {
    promptNames.forEach((promptName) => {
        const guidelineRegexp = new RegExp(`({{\\s*(${promptName})\\s*}})`, 'g');
        html = html.replace(guidelineRegexp, '<div collapse="$2">$1</div>');
    });
    return html;
}
